import React, { useMemo } from "react";

/**
 * TornEdge — déchirure de papier propre, 100% SVG.
 *
 * Pas de PNG, pas de masque CSS : un seul `path` est généré procéduralement
 * à partir d'une seed déterministe pour que la déchirure soit
 * - parfaitement collée au bord (pas de halo, pas de fond rectangulaire)
 * - sans bande sombre / claire visible (la couleur est *exactement* le fond
 *   de la section vers laquelle la déchirure pointe)
 * - responsive : le `path` est redessiné par le navigateur via `preserveAspectRatio="none"`
 *
 * Props:
 *  - position : "top" | "bottom"
 *  - color    : couleur de la "feuille suivante" (celle qui mange dans la précédente)
 *  - height   : hauteur en px (default 70)
 *  - seed     : entier — change la silhouette
 *  - shadow   : intensité de l'ombre (0 = aucune)
 */

// PRNG déterministe (mulberry32) pour avoir des silhouettes reproductibles.
function rng(seed) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// Génère le `d` du path représentant le bord déchiré (pour position="bottom").
// Le path forme un polygone qui couvre la moitié supérieure et présente une
// silhouette déchirée en haut. On le mirroir-Y pour position="top".
function buildTearPath(seed = 7, segments = 36, baseY = 60, ampMin = 14, ampMax = 38) {
  const rand = rng(seed);
  const W = 1000; // viewBox width
  const H = 100;  // viewBox height
  const pts = [];

  for (let i = 0; i <= segments; i++) {
    const x = (W * i) / segments;
    // Bruit "haute fréquence" + "basse fréquence" pour un bord crédible
    const lf = Math.sin(i * 0.45 + rand() * 6) * 8;
    const amp = ampMin + rand() * (ampMax - ampMin);
    const spike = (rand() - 0.5) * amp;
    const y = baseY - lf + spike;
    pts.push([x, Math.max(2, Math.min(H - 2, y))]);
  }

  // Build SVG path with smooth quadratic curves between points to suggest paper
  let d = `M 0 ${H} L 0 ${pts[0][1].toFixed(2)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[i + 1];
    const cx = (x1 + x2) / 2;
    const cy = (y1 + y2) / 2;
    d += ` Q ${x1.toFixed(2)} ${y1.toFixed(2)} ${cx.toFixed(2)} ${cy.toFixed(2)}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${W} ${last[1].toFixed(2)} L ${W} ${H} Z`;
  return d;
}

export function TornEdge({
  position = "bottom",
  color = "#ffffff",
  height = 70,
  className = "",
  seed = 7,
  shadow = 0.22,
  // legacy props accepted but ignored
  variant: _variant,
  flip: _flip,
  grain: _grain,
}) {
  const isTop = position === "top";
  const d = useMemo(() => buildTearPath(seed), [seed]);
  const filterId = `tearShadow-${seed}-${isTop ? "t" : "b"}`;

  return (
    <div
      className={`pointer-events-none absolute left-0 right-0 z-[5] ${isTop ? "top-[-1px]" : "bottom-[-1px]"} ${className}`}
      style={{ height }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 1000 100"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        style={{
          display: "block",
          transform: isTop ? "scaleY(-1)" : "none",
        }}
      >
        {shadow > 0 && (
          <defs>
            <filter id={filterId} x="-2%" y="-30%" width="104%" height="160%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
              <feOffset dy="3"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope={shadow}/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
        )}
        <path d={d} fill={color} filter={shadow > 0 ? `url(#${filterId})` : undefined}/>
      </svg>
    </div>
  );
}

export default TornEdge;
